import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import Text from 'components/_UI/Text';
import establishmentsApi from 'api/requests/establishments';
import eventsApi from 'api/requests/events';
import paths from 'routes/paths';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import { useEstablishmentsList } from 'context/establishments/EstablishmentsList';
import { useEstablishmentFilter } from 'context/establishments/EstablishmentFilter';
import EstablishmentsNearToYou from './nearToYou/EstablishmentsNearToYou';
import EstablishmentCategoriesList from './categories/EstablishmentCategoriesList';
import CitiesList from 'modules/home/CitiesList';
import EstablishmentCarousel from './carousel/EstablishmentCarousel';
import CarouselSkeleton from 'components/commons/skeletons/CarouselSkeleton';
import { useNavigate } from 'react-router-dom';

export default function EstablishmentsV2({
    onLoadData,
    onLoading,
}) {
    const { currentLocation, setCurrentLocation } = useCurrentLocation();
    const { setEstablishmentsList } = useEstablishmentsList();
    const { establishmentFilter } = useEstablishmentFilter();
    const isFirstFilterRender = useRef(true);
    const [establishmentData, setEstablishmentData] = useState();
    const [activeLocations, setActiveLocations] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        eventsApi.getActiveLocations()
            .then(({ data }) => setActiveLocations(data?.states || []))
            .catch(() => {});
    }, []);

    function getStateForCity(cityName) {
        for (const state of activeLocations) {
            if (state.cities?.some(c => c.name === cityName)) return state.code;
        }
        return '';
    }

    useEffect(() => {
        getEstablishments();
    }, [currentLocation?.city])

    useEffect(() => {
        if (isFirstFilterRender.current) {
            isFirstFilterRender.current = false;
            return;
        }
        if (establishmentFilter) {
            getFilteredEstablishments(establishmentFilter);
        } else {
            getEstablishments();
        }
    }, [establishmentFilter])

    async function getEstablishments() {
        onLoading(true);
        try {
            const { data } = await establishmentsApi.getForMap();
            const arraysWithMultiple = data?.data?.filter(obj => obj.hasOwnProperty('multiples'));
            const arraysWithoutMultiple = data?.data?.filter(obj => {
                return !obj.hasOwnProperty('multiples') && !obj.hasOwnProperty('noAddress');
            });

            const formatedObject = {
                data: arraysWithoutMultiple,
                withMultiples: arraysWithMultiple
            };

            onLoadData(formatedObject);
            setEstablishmentsList(formatedObject);
            setEstablishmentData(formatedObject);
        } catch (error) {
            console.error('Error fetching establishments:', error);
            setEstablishmentsList({ data: [], withMultiples: [] });
            setEstablishmentData({ data: [], withMultiples: [] });
        } finally {
            onLoading(false);
        }
    }

    async function getFilteredEstablishments(filter) {
        if (!currentLocation?.city) return;
        onLoading(true);
        try {
            const { data } = await establishmentsApi.getForMap(currentLocation.city, filter);
            const arraysWithMultiple = data?.data?.filter(obj => obj.hasOwnProperty('multiples'));
            const arraysWithoutMultiple = data?.data?.filter(obj => {
                return !obj.hasOwnProperty('multiples') && !obj.hasOwnProperty('noAddress');
            });

            const formatedObject = {
                data: arraysWithoutMultiple,
                withMultiples: arraysWithMultiple
            };

            onLoadData(formatedObject);
            setEstablishmentsList(formatedObject);
            setEstablishmentData(formatedObject);
        } catch (error) {
            console.error('Error fetching filtered establishments:', error);
            setEstablishmentsList({ data: [], withMultiples: [] });
            setEstablishmentData({ data: [], withMultiples: [] });
        } finally {
            onLoading(false);
        }
    }

    return (
        <>
            <EstablishmentsContainer>
                {establishmentData === undefined ? (
                    <Column>
                        <SectionTitle>Estabelecimentos em destaque</SectionTitle>
                        <CarouselSkeleton />
                    </Column>
                ) : establishmentData?.data?.length > 0 ? (
                    <Column>
                        <SectionTitle>Estabelecimentos em destaque</SectionTitle>
                        <EstablishmentCarousel
                            data={establishmentData.data.slice(0, 12)}
                            onSlideClick={(establishment) => navigate(`/estabelecimento/${establishment.slug}/detalhes`)}
                        />
                    </Column>
                ) : null}

                <Column marginTop='48px'>
                    <SectionHeader>
                        <SectionTitle>Estabelecimentos próximos a você</SectionTitle>
                        <VerTodosLink onClick={() => {
                            if (currentLocation?.city) {
                                const stateCode = currentLocation.state || getStateForCity(currentLocation.city);
                                const citySlug = [currentLocation.city, stateCode]
                                    .filter(Boolean).join('-').toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                                navigate(`${paths.establishments.search}?city=${encodeURIComponent(citySlug)}`);
                            } else {
                                navigate(paths.establishments.search);
                            }
                        }}>
                            Ver todos →
                        </VerTodosLink>
                    </SectionHeader>
                    <EstablishmentsNearToYou
                        onCardClick={(establishment) => navigate(`/estabelecimento/${establishment.slug}/detalhes`)}
                        onEventClick={() => {}}
                        searchEvents={() => {}}
                    />
                </Column>

                <Column marginTop='48px'>
                    <SectionTitle>Categorias</SectionTitle>
                    <EstablishmentCategoriesList />
                </Column>

                <Column marginTop='48px'>
                    <SectionTitle>O que fazer por aí?</SectionTitle>
                    <CitiesList />
                </Column>
            </EstablishmentsContainer>

        </>
    )
}

const EstablishmentsContainer = styled(Column)`
    width: 100%;
    height: auto;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 100%;
        display: flex;
        margin-top: 20px;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 12px 0;
    width: 100%;
`;

const VerTodosLink = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.75;
    }
`;
